import { inject, Injectable, signal } from '@angular/core'
import { Observable, Subject } from 'rxjs'
import { InterventionsService } from '../../interventions/services/interventions.service'
import { AvailabilityTools } from '../../../core/tools/availability-tools'
import {
  RouteInputModel,
  DistanceLineInputModel,
} from '../../../core/components/map-with-markers/map-with-markers.component'

/**
 * Route information with distance and duration
 */
export interface RouteInfo {
  distance_km: number
  duration_min: number
}

/**
 * Home departure information for route planning
 */
export interface HomeDepartureInfo {
  departureTime: Date
  firstEventTime: Date
  travelMin: number
  isTight: boolean
}

/**
 * Home return information for route planning
 */
export interface HomeReturnInfo {
  lastEventEndTime: Date
  returnTime: Date
  travelMin: number
}

/**
 * Projected return time when an affair is pre-selected at end of day
 */
export interface ProjectedReturnInfo {
  affairEndTime: Date
  returnTime: Date
  travelMin: number
}

/**
 * Cached data for daily route calculations
 */
export interface DailyDataCache {
  events: any[]
  routeInfo: RouteInfo | null
  distancesToAffair: Map<string, { distance_km: number; duration_min: number }>
}

/**
 * Coordinates for route calculation
 */
export interface Coordinates {
  latitude: number
  longitude: number
}

/**
 * Affair data required for route calculations
 */
export interface AffairRouteData {
  id?: number | string
  name?: string
  workSiteLatitude: number
  workSiteLongitude: number
}

/**
 * Service responsible for all route and distance calculations
 * Extracts complex routing logic from the technician select component
 */
@Injectable({
  providedIn: 'root',
})
export class RouteCalculationService {
  private interventionsService = inject(InterventionsService)

  // Signals for reactive state
  public dailyRoute = signal<RouteInputModel | null>(null)
  public projectedRoute = signal<RouteInputModel | null>(null)
  public distanceLines = signal<DistanceLineInputModel[]>([])

  // Cache for daily data by technician/day
  private dailyDataCache: Map<string, DailyDataCache> = new Map()

  // Distances from affair to daily events
  public distancesToAffair: Map<
    string,
    { distance_km: number; duration_min: number }
  > = new Map()

  // Current route info
  public dailyRouteInfo: RouteInfo | null = null
  public projectedRouteInfo: RouteInfo | null = null

  // Home departure/return info
  public currentRouteDeparture: HomeDepartureInfo | null = null
  public currentRouteReturn: HomeReturnInfo | null = null
  public projectedRouteReturn: ProjectedReturnInfo | null = null

  // Loading states
  public loadingDailyRoute = false
  public loadingDistancesToAffair = false

  /**
   * Generate cache key for technician/day combination
   */
  getDailyCacheKey(technicianId: number, date: Date): string {
    const dateStr = this.formatDateYMD(date)
    return `${technicianId}_${dateStr}`
  }

  /**
   * Format date as YYYY-MM-DD
   */
  private formatDateYMD(date: Date): string {
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    return `${year}-${month}-${day}`
  }

  /**
   * Get cached data for a technician/day combination
   */
  getCachedData(cacheKey: string): DailyDataCache | undefined {
    return this.dailyDataCache.get(cacheKey)
  }

  /**
   * Store data in cache
   */
  setCachedData(cacheKey: string, data: DailyDataCache): void {
    this.dailyDataCache.set(cacheKey, data)
  }

  /**
   * Clear all caches
   */
  clearCache(): void {
    this.dailyDataCache.clear()
    this.distancesToAffair.clear()
  }

  /**
   * Clear route state
   */
  clearDailyRoute(): void {
    this.dailyRoute.set(null)
    this.projectedRoute.set(null)
    this.dailyRouteInfo = null
    this.projectedRouteInfo = null
    this.currentRouteDeparture = null
    this.currentRouteReturn = null
    this.projectedRouteReturn = null
    this.distancesToAffair.clear()
    this.distanceLines.set([])
  }

  /**
   * Check if coordinates are valid (non-null and non-zero)
   */
  hasValidCoordinates(
    lat: number | null | undefined,
    lng: number | null | undefined
  ): boolean {
    return lat != null && lng != null && lat !== 0 && lng !== 0
  }

  /**
   * Calculate distances from the affair to each daily event
   * @param affair The affair to schedule
   * @param events The daily calendar events
   * @param cacheKey Optional cache key to store results
   * @returns Observable that completes when calculation is done
   */
  calculateDistancesToAffair(
    affair: AffairRouteData,
    events: any[],
    cacheKey?: string
  ): Observable<void> {
    const result$ = new Subject<void>()

    if (
      !this.hasValidCoordinates(
        affair.workSiteLatitude,
        affair.workSiteLongitude
      ) ||
      !events ||
      events.length === 0
    ) {
      this.distancesToAffair.clear()
      result$.next()
      result$.complete()
      return result$.asObservable()
    }

    // Prepare source (the affair to schedule)
    const source = {
      latitude: affair.workSiteLatitude,
      longitude: affair.workSiteLongitude,
    }

    // Prepare targets (daily events with valid coordinates)
    const targets = events
      .map((event, index) => ({
        event,
        index,
      }))
      .filter(({ event }) => {
        const lat = event.data?.workSiteLatitude || event.workSiteLatitude
        const lng = event.data?.workSiteLongitude || event.workSiteLongitude
        return this.hasValidCoordinates(+lat, +lng)
      })
      .map(({ event, index }) => ({
        id: `event-${index}`,
        latitude: +(event.data?.workSiteLatitude || event.workSiteLatitude),
        longitude: +(event.data?.workSiteLongitude || event.workSiteLongitude),
      }))

    if (targets.length === 0) {
      this.distancesToAffair.clear()
      result$.next()
      result$.complete()
      return result$.asObservable()
    }

    this.loadingDistancesToAffair = true

    this.interventionsService.distancesFromSource(source, targets).subscribe({
      next: (response) => {
        this.loadingDistancesToAffair = false

        if (response.status === 'success' && response.distances) {
          this.distancesToAffair.clear()
          response.distances.forEach((dist) => {
            this.distancesToAffair.set(dist.id, {
              distance_km: dist.distance_km,
              duration_min: dist.duration_min,
            })
          })

          // Update cache if key provided
          if (cacheKey) {
            const existingCache = this.dailyDataCache.get(cacheKey)
            if (existingCache) {
              existingCache.distancesToAffair = new Map(this.distancesToAffair)
            }
          }
        }

        result$.next()
        result$.complete()
      },
      error: (error) => {
        console.error('Error calculating distances to affair:', error)
        this.loadingDistancesToAffair = false
        result$.error(error)
      },
    })

    return result$.asObservable()
  }

  /**
   * Calculate current route departure and return times
   * @param events Daily calendar events
   * @param technician The technician
   */
  calculateCurrentRouteDeparture(
    events: any[],
    technician: any,
    startLocationOverride?: { lat: number; lon: number } | null,
    endLocationOverride?: { lat: number; lon: number } | null
  ): Observable<void> {
    this.currentRouteDeparture = null
    this.currentRouteReturn = null

    const result$ = new Subject<void>()

    if (!events || events.length === 0 || !technician) {
      result$.next()
      result$.complete()
      return result$.asObservable()
    }

    const homeLatitude = technician?.homeLatitude || technician?.home_latitude
    const homeLongitude =
      technician?.homeLongitude || technician?.home_longitude

    // Use override coordinates if provided (hotel), otherwise use home
    const departureLat = startLocationOverride?.lat ?? homeLatitude
    const departureLon = startLocationOverride?.lon ?? homeLongitude
    const returnLat = endLocationOverride?.lat ?? homeLatitude
    const returnLon = endLocationOverride?.lon ?? homeLongitude

    if (!this.hasValidCoordinates(+departureLat, +departureLon)) {
      result$.next()
      result$.complete()
      return result$.asObservable()
    }
    const sortedEvents = [...events].sort((a, b) => {
      const timeA = new Date(a.start || a.startDate).getTime()
      const timeB = new Date(b.start || b.startDate).getTime()
      return timeA - timeB
    })

    const firstEvent = sortedEvents[0]
    const lastEvent = sortedEvents[sortedEvents.length - 1]

    // Get first event time and coordinates
    const firstEventTime = new Date(firstEvent.start || firstEvent.startDate)

    // Get last event end time
    let lastEventEndTime: Date | null = null
    if (lastEvent.end) {
      lastEventEndTime = new Date(lastEvent.end)
    } else if (lastEvent.start || lastEvent.startDate) {
      // Estimate end time based on duration
      const startTime = new Date(lastEvent.start || lastEvent.startDate)
      let durationMinutes = 60 // Default 1 hour
      const durationStr = lastEvent.duration || lastEvent.data?.duration
      if (durationStr && typeof durationStr === 'string') {
        const parts = durationStr.split(':')
        if (parts.length >= 2) {
          durationMinutes = parseInt(parts[0], 10) * 60 + parseInt(parts[1], 10)
        }
      }
      lastEventEndTime = new Date(startTime.getTime() + durationMinutes * 60000)
    }

    // Get coordinates for first and last events
    const firstEventLat =
      firstEvent.data?.workSiteLatitude || firstEvent.workSiteLatitude
    const firstEventLng =
      firstEvent.data?.workSiteLongitude || firstEvent.workSiteLongitude
    const lastEventLat =
      lastEvent.data?.workSiteLatitude || lastEvent.workSiteLatitude
    const lastEventLng =
      lastEvent.data?.workSiteLongitude || lastEvent.workSiteLongitude

    // Build targets for distance calculation
    const targets: Array<{ id: string; latitude: number; longitude: number }> =
      []

    if (this.hasValidCoordinates(+firstEventLat, +firstEventLng)) {
      targets.push({
        id: 'first-event',
        latitude: +firstEventLat,
        longitude: +firstEventLng,
      })
    }

    if (this.hasValidCoordinates(+lastEventLat, +lastEventLng)) {
      targets.push({
        id: 'last-event',
        latitude: +lastEventLat,
        longitude: +lastEventLng,
      })
    }

    if (targets.length === 0) {
      result$.next()
      result$.complete()
      return result$.asObservable()
    }
    const source = {
      latitude: +departureLat,
      longitude: +departureLon,
    }

    // If return location is different from departure (e.g., hotel start + home end),
    // we need separate distance calculations
    const returnSource = {
      latitude: +returnLat,
      longitude: +returnLon,
    }
    const sameStartEnd =
      Math.abs(+departureLat - +returnLat) < 0.0001 &&
      Math.abs(+departureLon - +returnLon) < 0.0001

    this.interventionsService.distancesFromSource(source, targets).subscribe({
      next: (response) => {
        if (response.status === 'success' && response.distances) {
          // Process first event distance (departure from start location)
          const firstEventDist = response.distances.find(
            (d) => d.id === 'first-event'
          )
          if (firstEventDist) {
            const travelMin = Math.round(firstEventDist.duration_min)
            const departureTime = new Date(
              firstEventTime.getTime() - travelMin * 60000
            )
            const isTight =
              departureTime.getHours() < 7 ||
              (departureTime.getHours() === 7 &&
                departureTime.getMinutes() < 30)

            this.currentRouteDeparture = {
              departureTime,
              firstEventTime,
              travelMin,
              isTight,
            }
          }

          // Process last event distance (return to end location)
          if (sameStartEnd) {
            // Same start/end location: use the distance from the same source call
            const lastEventDist = response.distances.find(
              (d) => d.id === 'last-event'
            )
            if (lastEventDist && lastEventEndTime) {
              const travelMin = Math.round(lastEventDist.duration_min)
              const returnTime = new Date(
                lastEventEndTime.getTime() + travelMin * 60000
              )
              this.currentRouteReturn = {
                lastEventEndTime,
                returnTime,
                travelMin,
              }
            }
            result$.next()
            result$.complete()
          } else if (
            lastEventEndTime &&
            this.hasValidCoordinates(+lastEventLat, +lastEventLng)
          ) {
            // Different start/end: calculate return distance from last event to end location
            const lastEventSource = {
              latitude: +lastEventLat,
              longitude: +lastEventLng,
            }
            const returnTarget = [
              {
                id: 'return-location',
                latitude: +returnLat,
                longitude: +returnLon,
              },
            ]
            this.interventionsService
              .distancesFromSource(lastEventSource, returnTarget)
              .subscribe({
                next: (returnResponse) => {
                  if (
                    returnResponse.status === 'success' &&
                    returnResponse.distances
                  ) {
                    const returnDist = returnResponse.distances.find(
                      (d) => d.id === 'return-location'
                    )
                    if (returnDist && lastEventEndTime) {
                      const travelMin = Math.round(returnDist.duration_min)
                      const returnTime = new Date(
                        lastEventEndTime.getTime() + travelMin * 60000
                      )
                      this.currentRouteReturn = {
                        lastEventEndTime,
                        returnTime,
                        travelMin,
                      }
                    }
                  }
                  result$.next()
                  result$.complete()
                },
                error: () => {
                  result$.next()
                  result$.complete()
                },
              })
          } else {
            result$.next()
            result$.complete()
          }
        } else {
          result$.next()
          result$.complete()
        }
      },
      error: (error) => {
        console.error('Error calculating home distances:', error)
        result$.next()
        result$.complete()
      },
    })

    return result$.asObservable()
  }

  /**
   * Calculate projected return time when a slot is selected
   * @param slot The pre-selected slot
   * @param technician The technician
   * @param affair The affair to schedule
   * @param interventionDuration Duration of the intervention in minutes
   */
  calculateProjectedReturnTime(
    slot: any,
    technician: any,
    affair: AffairRouteData,
    interventionDuration: number,
    endLocationOverride?: { lat: number; lon: number } | null
  ): Observable<void> {
    this.projectedRouteReturn = null

    const result$ = new Subject<void>()

    if (!slot || !technician || !affair) {
      result$.next()
      result$.complete()
      return result$.asObservable()
    }

    const homeLatitude = technician?.homeLatitude || technician?.home_latitude
    const homeLongitude =
      technician?.homeLongitude || technician?.home_longitude

    // Use override coordinates if provided (hotel), otherwise use home
    const returnLat = endLocationOverride?.lat ?? homeLatitude
    const returnLon = endLocationOverride?.lon ?? homeLongitude

    if (!this.hasValidCoordinates(+returnLat, +returnLon)) {
      result$.next()
      result$.complete()
      return result$.asObservable()
    }

    // Calculate when the affair would end
    const slotStart = new Date(slot.start)
    const affairEndTime = new Date(
      slotStart.getTime() + interventionDuration * 60000
    )

    const affairLat = affair.workSiteLatitude
    const affairLng = affair.workSiteLongitude

    if (!this.hasValidCoordinates(affairLat, affairLng)) {
      result$.next()
      result$.complete()
      return result$.asObservable()
    }

    const source = {
      latitude: +affairLat,
      longitude: +affairLng,
    }

    const targets = [
      {
        id: 'home',
        latitude: +returnLat,
        longitude: +returnLon,
      },
    ]

    this.interventionsService.distancesFromSource(source, targets).subscribe({
      next: (response) => {
        if (response.status === 'success' && response.distances) {
          const homeDistance = response.distances.find((d) => d.id === 'home')
          if (homeDistance) {
            const travelMin = Math.round(homeDistance.duration_min)
            const returnTime = new Date(
              affairEndTime.getTime() + travelMin * 60000
            )

            this.projectedRouteReturn = {
              affairEndTime,
              returnTime,
              travelMin,
            }
          }
        }
        result$.next()
        result$.complete()
      },
      error: (error) => {
        console.error('Error calculating projected return time:', error)
        result$.next()
        result$.complete()
      },
    })

    return result$.asObservable()
  }

  /**
   * Update distance lines on the map from affair to each event
   */
  updateDistanceLines(affair: AffairRouteData, events: any[]): void {
    if (
      !this.hasValidCoordinates(
        affair.workSiteLatitude,
        affair.workSiteLongitude
      ) ||
      !events
    ) {
      this.distanceLines.set([])
      return
    }

    const affairCoords: [number, number] = [
      +affair.workSiteLatitude,
      +affair.workSiteLongitude,
    ]

    const lines: DistanceLineInputModel[] = []

    events.forEach((event, index) => {
      const eventId = `event-${index}`
      const distance = this.distancesToAffair.get(eventId)

      if (!distance) return

      const lat = event.data?.workSiteLatitude || event.workSiteLatitude
      const lng = event.data?.workSiteLongitude || event.workSiteLongitude

      if (!this.hasValidCoordinates(+lat, +lng)) return

      lines.push({
        from: affairCoords,
        to: [+lat, +lng],
        durationMin: distance.duration_min,
        color: '#3B82F6',
      })
    })

    this.distanceLines.set(lines)
  }

  /**
   * Calculate the daily route for events
   * @param userId The technician's user ID
   * @param events The calendar events for the day
   * @param users All users (to get technician color)
   * @param cacheKey Optional cache key to store results
   */
  calculateDailyRoute(
    userId: number,
    events: any[],
    users: any[],
    cacheKey?: string
  ): Observable<void> {
    const result$ = new Subject<void>()

    // Format interventions for the route API
    const interventionsForRoute = events.map((event, index) => ({
      id: event.id || `event-${index}`,
      title: event.title || event.name || 'Intervention',
      start: event.start || event.startDate,
      data: {
        workSiteLatitude:
          event.data?.workSiteLatitude || event.workSiteLatitude,
        workSiteLongitude:
          event.data?.workSiteLongitude || event.workSiteLongitude,
      },
    }))

    this.loadingDailyRoute = true

    this.interventionsService
      .getRoute(userId, interventionsForRoute)
      .subscribe({
        next: (routeResponse) => {
          this.loadingDailyRoute = false

          if (
            routeResponse?.geometry?.coordinates &&
            routeResponse.geometry.coordinates.length > 0
          ) {
            // Get technician color for the route
            const technician =
              events[0]?.usersTechnicians?.[0] ||
              users.find((u: any) => u.id === userId)
            const routeColor = technician?.color || '#ff6b35'

            this.dailyRoute.set({
              coordinates: routeResponse.geometry.coordinates,
              color: routeColor,
              weight: 4,
              opacity: 0.8,
            })

            // Store route summary info
            if (routeResponse.summary) {
              this.dailyRouteInfo = {
                distance_km: routeResponse.summary.distance_km,
                duration_min: routeResponse.summary.duration_min,
              }

              // Update cache with route info
              if (cacheKey) {
                const existingCache = this.dailyDataCache.get(cacheKey)
                if (existingCache) {
                  existingCache.routeInfo = this.dailyRouteInfo
                } else {
                  this.dailyDataCache.set(cacheKey, {
                    events,
                    routeInfo: this.dailyRouteInfo,
                    distancesToAffair: new Map(this.distancesToAffair),
                  })
                }
              }
            }
          } else {
            this.dailyRoute.set(null)
          }

          result$.next()
          result$.complete()
        },
        error: (error) => {
          console.error('Error calculating daily route:', error)
          this.loadingDailyRoute = false
          this.dailyRoute.set(null)
          result$.error(error)
        },
      })

    return result$.asObservable()
  }

  /**
   * Calculate the projected route including the affair to schedule
   * @param userId The technician's user ID
   * @param existingEvents The current calendar events for the day
   * @param preSelectedSlot The pre-selected time slot
   * @param affair The affair to schedule
   */
  calculateProjectedRoute(
    userId: number,
    existingEvents: any[],
    preSelectedSlot: any,
    affair: AffairRouteData
  ): Observable<void> {
    const result$ = new Subject<void>()

    // Only calculate projected route if a slot has been pre-selected
    if (!preSelectedSlot?.slot?.start) {
      this.projectedRoute.set(null)
      this.projectedRouteInfo = null
      result$.next()
      result$.complete()
      return result$.asObservable()
    }

    // Check if we have valid affair coordinates
    if (
      !this.hasValidCoordinates(
        affair.workSiteLatitude,
        affair.workSiteLongitude
      )
    ) {
      this.projectedRoute.set(null)
      this.projectedRouteInfo = null
      result$.next()
      result$.complete()
      return result$.asObservable()
    }

    // Get the start time from pre-selected slot
    const selectedStartTime = new Date(preSelectedSlot.slot.start).getTime()
    const selectedStartDate = preSelectedSlot.slot.start

    // Create affair event to insert into the route at the correct position
    const affairEvent = {
      id: `affair-to-schedule-${affair.id || 'new'}`,
      title: affair.name || 'Nouvelle intervention',
      start: selectedStartDate,
      data: {
        workSiteLatitude: affair.workSiteLatitude,
        workSiteLongitude: affair.workSiteLongitude,
      },
      isAffairToSchedule: true,
    }

    // Sort existing events by start time and insert affair at the correct chronological position
    const sortedEvents = [...existingEvents].sort((a, b) => {
      const timeA = new Date(a.start || a.startDate).getTime()
      const timeB = new Date(b.start || b.startDate).getTime()
      return timeA - timeB
    })

    // Find the correct position to insert the affair based on selected slot time
    let insertIndex = sortedEvents.length
    for (let i = 0; i < sortedEvents.length; i++) {
      const eventTime = new Date(
        sortedEvents[i].start || sortedEvents[i].startDate
      ).getTime()
      if (selectedStartTime < eventTime) {
        insertIndex = i
        break
      }
    }

    // Insert affair at the correct position
    const allEvents = [
      ...sortedEvents.slice(0, insertIndex),
      affairEvent,
      ...sortedEvents.slice(insertIndex),
    ]

    // Format interventions for the route API
    const interventionsForRoute = allEvents.map((event, index) => ({
      id: event.id || `event-${index}`,
      title: event.title || event.name || 'Intervention',
      start: event.start || event.startDate,
      data: {
        workSiteLatitude:
          event.data?.workSiteLatitude || event.workSiteLatitude,
        workSiteLongitude:
          event.data?.workSiteLongitude || event.workSiteLongitude,
      },
    }))

    this.interventionsService
      .getRoute(userId, interventionsForRoute)
      .subscribe({
        next: (routeResponse) => {
          if (
            routeResponse?.geometry?.coordinates &&
            routeResponse.geometry.coordinates.length > 0
          ) {
            // Use green dashed line for projected route
            this.projectedRoute.set({
              coordinates: routeResponse.geometry.coordinates,
              color: '#10B981',
              weight: 4,
              opacity: 0.8,
              dashArray: '10, 10',
            })

            // Store projected route summary info
            if (routeResponse.summary) {
              this.projectedRouteInfo = {
                distance_km: routeResponse.summary.distance_km,
                duration_min: routeResponse.summary.duration_min,
              }
            }
          } else {
            this.projectedRoute.set(null)
            this.projectedRouteInfo = null
          }

          result$.next()
          result$.complete()
        },
        error: (error) => {
          console.error('Error calculating projected route:', error)
          this.projectedRoute.set(null)
          this.projectedRouteInfo = null
          result$.error(error)
        },
      })

    return result$.asObservable()
  }

  /**
   * Get sorted daily events
   */
  getSortedDailyEvents(events: any[]): any[] {
    if (!events?.length) return []
    return AvailabilityTools.sortEventsByStartTime(events)
  }
}
